import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import fotoService from '../services/fotoService';
import AnimatedNumber from '../components/AnimatedNumber';
import './FotosUpload.css';

// Por encima de este número de archivos, no se generan miniaturas individuales
// (evita saturar la memoria del navegador con lotes grandes, ej. 3800 fotos).
const UMBRAL_SIN_PREVIEW = 200;

// Formatos RAW de cámara: no se pueden mostrar en el navegador ni procesar
// (OCR/marca de agua). Las cámaras suelen guardar RAW+JPG de la misma toma,
// así que el JPG hermano ya cubre esa foto - el RAW solo estorba.
const EXTENSIONES_RAW = ['.dng', '.cr2', '.cr3', '.nef', '.arw', '.raf', '.rw2', '.orf', '.srw', '.pef'];

const esArchivoRaw = (file) => {
  const nombre = file.name.toLowerCase();
  return EXTENSIONES_RAW.some(ext => nombre.endsWith(ext));
};

const filtrarImagenes = (fileList) => {
  const todos = Array.from(fileList);
  const raw = todos.filter(esArchivoRaw);
  const validas = todos.filter(file => !esArchivoRaw(file) && file.type.startsWith('image/'));
  if (raw.length > 0) {
    alert(
      `⚠️ Se omitieron ${raw.length} archivo(s) RAW (.dng/.cr2/.nef/etc.). ` +
      `Estos formatos no se pueden procesar ni previsualizar - normalmente tu cámara ya guarda ` +
      `también la versión .jpg de la misma foto, así que no se pierde nada.`
    );
  }
  return validas;
};

// Convierte segundos a formato mm:ss para el temporizador de subida
const formatTiempo = (segundos) => {
  if (segundos == null || !isFinite(segundos) || segundos < 0) return '--:--';
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatTamano = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const CONSEJOS = [
  'Mantén esta pestaña abierta mientras subimos tus fotos.',
  'Optimizamos cada foto antes de subirla para que todo sea más rápido.',
  'Al terminar, detectaremos los dorsales automáticamente.',
  'Comparte el link o QR del evento cuando tus fotos estén listas.'
];

const RADIO_ANILLO = 54;
const CIRCUNFERENCIA = 2 * Math.PI * RADIO_ANILLO;

const UploadOverlay = ({ progreso, tiempoTranscurrido }) => {
  const [consejo, setConsejo] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setConsejo(c => (c + 1) % CONSEJOS.length), 4000);
    return () => clearInterval(id);
  }, []);

  const { actual, total, fallidos, bytesSubidos = 0, bytesTotal = 0 } = progreso;
  const preparando = bytesSubidos === 0 && actual === 0;
  const fraccion = bytesTotal > 0 ? bytesSubidos / bytesTotal : (total > 0 ? actual / total : 0);
  const porcentaje = Math.min(100, Math.round(fraccion * 100));
  const bytesPorSeg = tiempoTranscurrido > 0 ? bytesSubidos / tiempoTranscurrido : 0;
  const restante = bytesPorSeg > 0 ? (bytesTotal - bytesSubidos) / bytesPorSeg : null;

  return (
    <div className="up-overlay" role="status" aria-live="polite">
      <div className="up-overlay-card">
        <div className="up-ring">
          <svg viewBox="0 0 128 128">
            <circle className="up-ring-bg" cx="64" cy="64" r={RADIO_ANILLO} />
            <circle
              className="up-ring-fg"
              cx="64"
              cy="64"
              r={RADIO_ANILLO}
              strokeDasharray={CIRCUNFERENCIA}
              strokeDashoffset={CIRCUNFERENCIA * (1 - porcentaje / 100)}
            />
          </svg>
          <div className="up-ring-label">
            <strong><AnimatedNumber value={porcentaje} duration={600} />%</strong>
            <span>{actual} de {total} listas</span>
          </div>
        </div>

        <h3>{preparando ? 'Preparando tus fotos…' : 'Subiendo tus fotos…'}</h3>
        <p className="up-tip" key={preparando ? 'preparando' : consejo}>
          {preparando ? 'Optimizando cada foto para que la subida sea más rápida.' : CONSEJOS[consejo]}
        </p>

        <div className="up-bar"><div className="up-bar-fill" style={{ width: `${porcentaje}%` }} /></div>

        <div className="up-stats">
          <div><span>Transcurrido</span><strong>{formatTiempo(tiempoTranscurrido)}</strong></div>
          <div><span>Restante</span><strong>{restante !== null ? formatTiempo(restante) : 'calculando…'}</strong></div>
          <div><span>Subido</span><strong>{formatTamano(bytesSubidos)} / {formatTamano(bytesTotal)}</strong></div>
          <div><span>Velocidad</span><strong>{bytesPorSeg > 0 ? `${formatTamano(bytesPorSeg)}/s` : '—'}</strong></div>
          {fallidos > 0 && <div className="up-stat-warn"><span>Con errores</span><strong>{fallidos}</strong></div>}
        </div>
      </div>
    </div>
  );
};

const FotosUpload = () => {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [progreso, setProgreso] = useState({ actual: 0, total: 0, fallidos: 0 });
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const tiempoInicioRef = useRef(null);
  const intervaloRef = useRef(null);

  useEffect(() => {
    if (!uploading) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [uploading]);
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(filtrarImagenes(e.dataTransfer.files));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(filtrarImagenes(e.target.files));
    }
    // Permite volver a seleccionar la misma carpeta/archivos sin recargar la página
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    // Con lotes muy grandes, generar una vista previa (createObjectURL + <img>)
    // por cada archivo puede congelar o tumbar la pestaña del navegador.
    const generarPreview = newFiles.length <= UMBRAL_SIN_PREVIEW;
    const filesWithPreview = newFiles.map(file => ({
      file,
      preview: generarPreview ? URL.createObjectURL(file) : null,
      id: Math.random().toString(36),
      numeroDorsal: ''
    }));
    setFiles(prev => [...prev, ...filesWithPreview]);
  };

  const updateNumeroDorsal = (id, valor) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, numeroDorsal: valor } : f));
  };

  const removeFile = (id) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== id);
      // Liberar URL del objeto para evitar fugas de memoria
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return updated;
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      alert('Por favor selecciona al menos una foto');
      return;
    }

    setUploading(true);
    setProgreso({ actual: 0, total: files.length, fallidos: 0 });
    setTiempoTranscurrido(0);
    tiempoInicioRef.current = Date.now();
    intervaloRef.current = setInterval(() => {
      setTiempoTranscurrido(Math.floor((Date.now() - tiempoInicioRef.current) / 1000));
    }, 1000);
    try {
      const fileObjects = files.map(f => f.file);
      const dorsales = files.map(f => f.numeroDorsal || '');
      const { lotesFallidos } = await fotoService.uploadFotos(eventoId, fileObjects, dorsales, (actual, total, fallidos, bytes) => {
        setProgreso({ actual, total, fallidos, ...bytes });
      });

      // Liberar todas las URLs de objetos (solo se generaron si hubo preview)
      files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));

      if (lotesFallidos.length > 0) {
        const totalFallidas = lotesFallidos.reduce((acc, l) => acc + l.archivos.length, 0);
        const nombresFallidos = lotesFallidos.flatMap(l => l.archivos).slice(0, 20).join(', ');
        alert(
          `⚠️ Se subieron ${files.length - totalFallidas} de ${files.length} foto(s). ` +
          `${totalFallidas} fallaron después de varios reintentos (por ej.: ${nombresFallidos}${totalFallidas > 20 ? '...' : ''}). ` +
          `Vuelve a seleccionar e intentar subir solo esas fotos.`
        );
      } else {
        alert(`✅ ${files.length} foto(s) subidas exitosamente. Se están procesando...`);
      }
      navigate(`/eventos/${eventoId}/fotos`);
    } catch (err) {
      alert('Error al subir fotos: ' + (err.response?.data?.mensaje || err.message));
    } finally {
      clearInterval(intervaloRef.current);
      intervaloRef.current = null;
      setUploading(false);
      setProgreso({ actual: 0, total: 0, fallidos: 0 });
    }
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <button onClick={() => navigate(`/eventos/${eventoId}/fotos`)} className="button-back">
          ← Volver
        </button>
        <div>
          <h1>Subir fotos al evento</h1>
          <p className="upload-subtitle">Sube tus fotos y nosotros las preparamos para venderlas por WhatsApp.</p>
        </div>
      </div>

      <ol className="up-steps">
        <li className={`up-step ${files.length > 0 || uploading ? 'done' : 'active'}`}><span>1</span>Elige tus fotos</li>
        <li className={`up-step ${uploading ? 'active' : ''}`}><span>2</span>Subimos y optimizamos</li>
        <li className="up-step"><span>3</span>Listas para vender</li>
      </ol>

      <div className="upload-card">
        <form onSubmit={handleUpload}>
          {/* Dropzone */}
          <div
            className={`dropzone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              id="file-input"
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <input
              id="folder-input"
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            <div className="dropzone-content">
              <div className="dropzone-icon">
                📸
              </div>
              <p className="dropzone-title">
                Arrastra y suelta tus fotos aquí
              </p>
              <p className="dropzone-subtitle">
                o haz clic para seleccionar archivos
              </p>
              <div className="dropzone-buttons">
                <button
                  type="button"
                  className="button-browse"
                  onClick={() => document.getElementById('file-input').click()}
                >
                  Seleccionar Archivos
                </button>
                <button
                  type="button"
                  className="button-browse button-browse-folder"
                  onClick={() => document.getElementById('folder-input').click()}
                >
                  Seleccionar Carpeta Completa
                </button>
              </div>
              <p className="dropzone-info">
                Formatos soportados: JPG, PNG, JPEG • Múltiples archivos permitidos<br />
                ¿Windows no te deja seleccionar varias fotos a la vez? Usa "Seleccionar Carpeta Completa"
              </p>
            </div>
          </div>

          {/* Preview de archivos seleccionados */}
          {files.length > 0 && (
            <div className="files-preview-section">
              <div className="preview-header">
                <h3>
                  {files.length} foto(s) seleccionada(s)
                  <span className="up-total-size">{formatTamano(files.reduce((s, f) => s + f.file.size, 0))}</span>
                </h3>
                <button
                  type="button"
                  className="button-clear-all"
                  onClick={() => {
                    files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
                    setFiles([]);
                  }}
                >
                  Limpiar todo
                </button>
              </div>

              {files.length > UMBRAL_SIN_PREVIEW && (
                <p className="dropzone-info">
                  ⚠️ Lote grande: se omiten las miniaturas para no saturar el navegador.
                </p>
              )}

              <div className="preview-grid">
                {files.map((fileObj) => (
                  <div key={fileObj.id} className="preview-item">
                    {fileObj.preview
                      ? <img src={fileObj.preview} alt={fileObj.file.name} />
                      : <div className="preview-item-sin-imagen">📄</div>}
                    <div className="preview-overlay">
                      <p className="preview-name">{fileObj.file.name}</p>
                      <p className="preview-size">
                        {(fileObj.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <div className="preview-dorsal-row">
                      <span className="preview-dorsal-label">#</span>
                      <input
                        type="text"
                        className="preview-dorsal-input"
                        placeholder="Nº dorsal"
                        value={fileObj.numeroDorsal}
                        onChange={e => updateNumeroDorsal(fileObj.id, e.target.value.replace(/[^0-9]/g, ''))}
                        onClick={e => e.stopPropagation()}
                        maxLength={5}
                      />
                    </div>
                    <button
                      type="button"
                      className="preview-remove"
                      onClick={() => removeFile(fileObj.id)}
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botón de subir */}
          {files.length > 0 && (
            <div className="upload-actions">
              <button 
                type="submit" 
                className="button-upload" 
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span className="spinner"></span>
                    Subiendo...
                  </>
                ) : (
                  <>
                    ⬆️ Subir {files.length} foto(s)
                  </>
                )}
              </button>
            </div>
          )}
        </form>

        {uploading && progreso.total > 0 && (
          <UploadOverlay progreso={progreso} tiempoTranscurrido={tiempoTranscurrido} />
        )}

        {/* Información */}
        <div className="upload-info">
          <h3>📋 Información del proceso:</h3>
          <ul>
            <li>✅ Las fotos se subirán de forma segura</li>
            <li>🤖 Se procesarán automáticamente para detectar números de corredor</li>
            <li>⏱️ El procesamiento puede tomar algunos minutos</li>
            <li>🔒 Tus fotos estarán protegidas y solo tú podrás administrarlas</li>
            <li>💡 Puedes subir múltiples fotos a la vez (recomendado: máx 50 por lote)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FotosUpload;
